import React, { useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  makeStyles
} from "@material-ui/core";
import {
  Add,
  Delete,
  People,
  Visibility,
  Edit,
  Send,
  Business,
  Security,
  SettingsEthernet,
  Assignment,
  CheckCircle,
  Warning,
  Info,
  Group,
  FileCopy
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(3),
    color: theme.palette.primary.main,
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  addButton: {
    marginBottom: theme.spacing(2),
  },
  testUsersList: {
    marginBottom: theme.spacing(3),
  },
  testUserItem: {
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px dashed ${theme.palette.divider}`,
  },
  reviewSection: {
    marginTop: theme.spacing(4),
    padding: 0,
    backgroundColor: 'transparent',
  },
  configCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    backgroundColor: 'white',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  configHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  configTitle: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  configIcon: {
    marginRight: theme.spacing(1),
    fontSize: '1.2rem',
  },
  configDetail: {
    marginBottom: theme.spacing(0.5),
  },
  statusChip: {
    fontSize: '0.75rem',
    height: 24,
  },
  summarySection: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  divider: {
    margin: theme.spacing(4, 0),
  },
  submitButton: {
    background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
    color: 'white',
    minWidth: '200px',
    height: '48px',
    fontSize: '1rem',
    fontWeight: 600,
    '&:hover': {
      background: 'linear-gradient(45deg, #388e3c 30%, #66bb6a 90%)',
    },
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    minHeight: '40px',
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5),
    '&:hover': {
      borderColor: theme.palette.text.primary,
    },
    '&.focused': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
      padding: theme.spacing(0.5) - 1, // Compensate for thicker border
    },
  },
  inputField: {
    border: 'none',
    outline: 'none',
    fontSize: '0.875rem',
    fontFamily: theme.typography.fontFamily,
    minWidth: '120px',
    flex: 1,
    padding: theme.spacing(0.5),
    backgroundColor: 'transparent',
  },
  chipInside: {
    height: '24px',
    fontSize: '0.75rem',
  }
}));

const TestingValidation = ({
  testUsers,
  formData,
  onTestUsersUpdate,
  onEdit,
  onSubmit,
  submitted,
  canSubmit,
  submissionResult
}) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [userFormData, setUserFormData] = useState({
    email: "",
    adGroups: []
  });
  const [newGroupInput, setNewGroupInput] = useState("");
  const [focusedInput, setFocusedInput] = useState(false);

  const handleAddUser = () => {
    setUserFormData({ email: "", adGroups: [] });
    setNewGroupInput("");
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleEditUser = (index) => {
    const user = testUsers[index];
    // Convert legacy groups string to adGroups array if needed
    if (user.groups && typeof user.groups === 'string' && !user.adGroups) {
      setUserFormData({
        email: user.email,
        adGroups: user.groups.split(',').map(g => g.trim()).filter(g => g)
      });
    } else {
      setUserFormData({
        email: user.email,
        adGroups: user.adGroups || []
      });
    }
    setNewGroupInput("");
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleDeleteUser = (index) => {
    const newData = testUsers.filter((_, i) => i !== index);
    onTestUsersUpdate(newData);
  };

  const handleSaveUser = () => {
    if (!userFormData.email.trim()) return;

    let newData;
    if (editingIndex !== null) {
      newData = [...testUsers];
      newData[editingIndex] = userFormData;
    } else {
      newData = [...testUsers, userFormData];
    }

    onTestUsersUpdate(newData);
    setDialogOpen(false);
  };

  const handleCancelUser = () => {
    setDialogOpen(false);
    setUserFormData({ email: "", adGroups: [] });
    setNewGroupInput("");
  };

  const handleSubmitRequest = () => {
    onSubmit();
  };

  const handleCopyRequestId = async () => {
    if (submissionResult && submissionResult.requestId) {
      try {
        await navigator.clipboard.writeText(submissionResult.requestId);
        // You could add a snackbar notification here if needed
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = submissionResult.requestId;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    }
  };

  const handleAddGroup = () => {
    if (!newGroupInput || !newGroupInput.trim()) return;

    const currentGroups = userFormData.adGroups || [];

    // Check if group already exists
    if (currentGroups.includes(newGroupInput.trim())) return;

    setUserFormData({
      ...userFormData,
      adGroups: [...currentGroups, newGroupInput.trim()]
    });

    // Clear input
    setNewGroupInput('');
  };

  const handleRemoveGroup = (groupIndex) => {
    const currentGroups = [...(userFormData.adGroups || [])];
    currentGroups.splice(groupIndex, 1);
    setUserFormData({
      ...userFormData,
      adGroups: currentGroups
    });
  };

  const handleGroupInputChange = (value) => {
    setNewGroupInput(value);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddGroup();
    }
  };

  const handleFocus = () => {
    setFocusedInput(true);
  };

  const handleBlur = () => {
    setFocusedInput(false);
  };

  // Helper functions to check completion status
  const isClientConfigComplete = () => {
    const clientData = formData.clientSetup;
    const idpData = formData.idpConfig;

    return (
      clientData && clientData.clientName && clientData.clientName.trim().length >= 2 &&
      clientData.clientCode && /^[a-zA-Z0-9_-]{2,20}$/.test(clientData.clientCode.trim()) &&
      clientData.environment &&
      clientData.technicalContact && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientData.technicalContact.trim()) &&
      clientData.hostingType &&
      idpData && idpData.provider && idpData.protocol &&
      idpData.tenantId && idpData.tenantId.trim().length >= 3
    );
  };

  const isIntegrationConfigComplete = () => {
    return (
      formData.applications && formData.applications.length > 0 &&
      formData.integrationDetails && formData.integrationDetails.spEntityId
    );
  };

  const isAccessConfigComplete = () => {
    const hasValidRoleMappings = formData.roleMappings && formData.roleMappings.length > 0 &&
      formData.roleMappings.every(mapping => {
        const hasValidGroups = Array.isArray(mapping.adGroups)
          ? mapping.adGroups.length > 0 && mapping.adGroups.some(group => group && group.trim())
          : (mapping.adGroup && mapping.adGroup.trim()); // Backward compatibility

        return mapping.application && hasValidGroups && mapping.role;
      });

    const hasValidClaimMappings = formData.claimMappings &&
      Object.keys(formData.claimMappings).filter(key => formData.claimMappings[key]).length > 0;

    return hasValidRoleMappings && hasValidClaimMappings;
  };

  const getConfigSummary = () => {
    const summary = [];

    if (formData.clientSetup && formData.clientSetup.clientName) {
      summary.push(`Client: ${formData.clientSetup.clientName}`);
    }

    if (formData.idpConfig && formData.idpConfig.provider) {
      summary.push(`Provider: ${formData.idpConfig.provider}`);
    }

    if (formData.idpConfig && formData.idpConfig.protocol) {
      summary.push(`Protocol: ${formData.idpConfig.protocol}`);
    }

    if (formData.applications && formData.applications.length) {
      summary.push(`Applications: ${formData.applications.length}`);
    }

    if (formData.roleMappings && formData.roleMappings.length) {
      summary.push(`Role Mappings: ${formData.roleMappings.length}`);
    }

    return summary;
  };

  return (
    <Box className={classes.root}>
      {/* Test Users Section */}
      <div className={classes.sectionHeader}>
        <People className={classes.sectionIcon} />
        <Typography className={classes.sectionTitle}>
          Test Users for SSO Integration
        </Typography>
      </div>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Provide test users who will be used to validate the SSO integration once it's configured.
        These users should have appropriate access in your Identity Provider.
      </Typography>

      <Button
        variant="contained"
        color="primary"
        startIcon={<Add />}
        onClick={handleAddUser}
        className={classes.addButton}
        size="small"
      >
        Add Test User
      </Button>

      {testUsers.length === 0 ? (
        <div className={classes.emptyState}>
          <People style={{ fontSize: 36, marginBottom: 8 }} color="disabled" />
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            No test users configured
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Add test users that will be used to validate the SSO integration.
          </Typography>
        </div>
      ) : (
        <List className={classes.testUsersList}>
          {testUsers.map((user, index) => (
            <ListItem
              key={index}
              className={classes.testUserItem}
              dense
            >
              <ListItemText
                primary={user.email}
                secondary={
                  user.adGroups && user.adGroups.length > 0
                    ? `Groups: ${user.adGroups.join(', ')}`
                    : user.groups
                      ? `Groups: ${user.groups}`
                      : "No groups specified"
                }
                primaryTypographyProps={{ variant: "subtitle2", color: "primary" }}
                secondaryTypographyProps={{ variant: "body2" }}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="edit"
                  onClick={() => handleEditUser(index)}
                  size="small"
                >
                  <Edit />
                </IconButton>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleDeleteUser(index)}
                  color="secondary"
                  size="small"
                >
                  <Delete />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Divider className={classes.divider} />

      {/* Configuration Review Section */}
      <div className={classes.sectionHeader}>
        <Visibility className={classes.sectionIcon} />
        <Typography className={classes.sectionTitle}>
          Configuration Review
        </Typography>
      </div>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Review your SSO configuration details below. You can navigate back to any previous step to complete missing information or make changes before submitting your integration request.
      </Typography>

      <div className={classes.reviewSection}>
        {/* Overall Status Summary */}
        <Paper className={classes.summarySection}>
          <Typography variant="h6" gutterBottom style={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle style={{ marginRight: 8, color: '#4caf50' }} />
            Configuration Summary
          </Typography>
          <div style={{ marginTop: 16 }}>
            {getConfigSummary().map((item, index) => (
              <Chip
                key={index}
                label={item}
                style={{ margin: 4 }}
                color="primary"
                variant="outlined"
              />
            ))}
          </div>
        </Paper>

        <Grid container spacing={3}>
          {/* Client Configuration Card */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.configCard}>
              <div className={classes.configHeader}>
                <Typography className={classes.configTitle}>
                  <Business className={classes.configIcon} />
                  Client Configuration
                </Typography>
                <Chip
                  label={isClientConfigComplete() ? "Complete" : "Incomplete"}
                  className={classes.statusChip}
                  color={isClientConfigComplete() ? "primary" : "default"}
                  icon={isClientConfigComplete() ? <CheckCircle /> : <Warning />}
                />
              </div>
              <div className={classes.configDetail}>
                <Typography variant="body2" color="textSecondary">Client Name:</Typography>
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {formData.clientSetup && formData.clientSetup.clientName || "Not configured"}
                </Typography>
              </div>
              <div className={classes.configDetail}>
                <Typography variant="body2" color="textSecondary">Environment:</Typography>
                <Typography variant="body1">
                  {formData.clientSetup && formData.clientSetup.environment || "Not configured"}
                </Typography>
              </div>
              <div className={classes.configDetail}>
                <Typography variant="body2" color="textSecondary">Hosting Type:</Typography>
                <Typography variant="body1">
                  {formData.clientSetup && formData.clientSetup.hostingType || "Not configured"}
                </Typography>
              </div>
              <div className={classes.configDetail}>
                <Typography variant="body2" color="textSecondary">Technical Contact:</Typography>
                <Typography variant="body1">
                  {formData.clientSetup && formData.clientSetup.technicalContact || "Not configured"}
                </Typography>
              </div>
              <Button
                size="small"
                onClick={() => onEdit(0)}
                style={{ marginTop: 12 }}
                variant="outlined"
              >
                Edit Client Setup
              </Button>
            </Paper>
          </Grid>

          {/* Identity Provider Card */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.configCard}>
              <div className={classes.configHeader}>
                <Typography className={classes.configTitle}>
                  <Security className={classes.configIcon} />
                  Identity Provider
                </Typography>
                <Chip
                  label={isClientConfigComplete() ? "Complete" : "Incomplete"}
                  className={classes.statusChip}
                  color={isClientConfigComplete() ? "primary" : "default"}
                  icon={isClientConfigComplete() ? <CheckCircle /> : <Warning />}
                />
              </div>
              <div className={classes.configDetail}>
                <Typography variant="body2" color="textSecondary">Provider:</Typography>
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {formData.idpConfig && formData.idpConfig.provider || "Not configured"}
                </Typography>
              </div>
              <div className={classes.configDetail}>
                <Typography variant="body2" color="textSecondary">Protocol:</Typography>
                <Typography variant="body1">
                  {formData.idpConfig && formData.idpConfig.protocol || "Not configured"}
                </Typography>
              </div>
              <div className={classes.configDetail}>
                <Typography variant="body2" color="textSecondary">Tenant/Organization:</Typography>
                <Typography variant="body1">
                  {formData.idpConfig && formData.idpConfig.tenantId || "Not configured"}
                </Typography>
              </div>
              <Button
                size="small"
                onClick={() => onEdit(0)}
                style={{ marginTop: 12 }}
                variant="outlined"
              >
                Edit IdP Setup
              </Button>
            </Paper>
          </Grid>

          {/* Integration Details Card */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.configCard}>
              <div className={classes.configHeader}>
                <Typography className={classes.configTitle}>
                  <SettingsEthernet className={classes.configIcon} />
                  Applications & Integration
                </Typography>
                <Chip
                  label={isIntegrationConfigComplete() ? "Complete" : "Incomplete"}
                  className={classes.statusChip}
                  color={isIntegrationConfigComplete() ? "primary" : "default"}
                  icon={isIntegrationConfigComplete() ? <CheckCircle /> : <Warning />}
                />
              </div>
              <div className={classes.configDetail}>
                <Typography variant="body2" color="textSecondary">Applications:</Typography>
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {formData.applications && formData.applications.length || 0} configured
                </Typography>
              </div>
              <div className={classes.configDetail}>
                <Typography variant="body2" color="textSecondary">Integration URLs:</Typography>
                <Typography variant="body1">
                  {formData.integrationDetails && formData.integrationDetails.spEntityId ? "Generated" : "Not generated"}
                </Typography>
              </div>
              {formData.applications && formData.applications.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Typography variant="caption" color="textSecondary">Applications:</Typography>
                  {formData.applications.slice(0, 2).map((app, index) => (
                    <Typography key={index} variant="caption" display="block" style={{ marginLeft: 8 }}>
                      • {app.name}
                    </Typography>
                  ))}
                  {formData.applications.length > 2 && (
                    <Typography variant="caption" style={{ marginLeft: 8, fontStyle: 'italic' }}>
                      +{formData.applications.length - 2} more
                    </Typography>
                  )}
                </div>
              )}
              <Button
                size="small"
                onClick={() => onEdit(1)}
                style={{ marginTop: 12 }}
                variant="outlined"
              >
                Edit Integration
              </Button>
            </Paper>
          </Grid>

          {/* Access Control Card */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.configCard}>
              <div className={classes.configHeader}>
                <Typography className={classes.configTitle}>
                  <Assignment className={classes.configIcon} />
                  Access Control
                </Typography>
                <Chip
                  label={isAccessConfigComplete() ? "Complete" : "Incomplete"}
                  className={classes.statusChip}
                  color={isAccessConfigComplete() ? "primary" : "default"}
                  icon={isAccessConfigComplete() ? <CheckCircle /> : <Warning />}
                />
              </div>
              <div className={classes.configDetail}>
                <Typography variant="body2" color="textSecondary">Role Mappings:</Typography>
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {formData.roleMappings && formData.roleMappings.length || 0} configured
                </Typography>
              </div>
              <div className={classes.configDetail}>
                <Typography variant="body2" color="textSecondary">Claim Mappings:</Typography>
                <Typography variant="body1">
                  {Object.keys(formData.claimMappings || {}).filter(key => formData.claimMappings[key]).length} mapped
                </Typography>
              </div>
              <Button
                size="small"
                onClick={() => onEdit(2)}
                style={{ marginTop: 12 }}
                variant="outlined"
              >
                Edit Access & Claims
              </Button>
            </Paper>
          </Grid>

          {/* Test Users Card */}
          <Grid item xs={12}>
            <Paper className={classes.configCard}>
              <div className={classes.configHeader}>
                <Typography className={classes.configTitle}>
                  <People className={classes.configIcon} />
                  Test Users
                </Typography>
                <Chip
                  label={testUsers.length > 0 ? `${testUsers.length} Users` : "No Users"}
                  className={classes.statusChip}
                  color={testUsers.length > 0 ? "primary" : "default"}
                  icon={testUsers.length > 0 ? <CheckCircle /> : <Info />}
                />
              </div>
              <div className={classes.configDetail}>
                <Typography variant="body2" color="textSecondary">
                  {testUsers.length > 0
                    ? "Test users have been configured and will be used to validate the SSO integration once it's set up."
                    : "No test users configured. Consider adding test users to help validate the integration."}
                </Typography>
              </div>
              {testUsers.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Typography variant="caption" color="textSecondary">Test Users:</Typography>
                  {testUsers.slice(0, 3).map((user, index) => (
                    <Typography key={index} variant="caption" display="block" style={{ marginLeft: 8 }}>
                      • {user.email}
                    </Typography>
                  ))}
                  {testUsers.length > 3 && (
                    <Typography variant="caption" style={{ marginLeft: 8, fontStyle: 'italic' }}>
                      +{testUsers.length - 3} more
                    </Typography>
                  )}
                </div>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Submit Request Section */}
        <Paper style={{ padding: 32, textAlign: 'center', marginTop: 24, background: submitted ? 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c8 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #e3f2fd 100%)' }}>
          {!submitted ? (
            <>
              <Typography variant="h5" gutterBottom style={{ color: '#1976d2', fontWeight: 600 }}>
                Submit SSO Integration Request
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph style={{ maxWidth: 600, margin: '0 auto 24px' }}>
                {canSubmit
                  ? "Ready to submit your SSO integration request. Click the button below to send your configuration to our integration team."
                  : "Please complete all required configuration sections above before submitting your integration request."
                }
              </Typography>
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={handleSubmitRequest}
                className={classes.submitButton}
                disabled={!canSubmit}
              >
                Submit Integration Request
              </Button>
              {!canSubmit && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 16, display: 'block', textAlign: 'center', maxWidth: 500, margin: '16px auto 0' }}>
                  Review the configuration sections above and complete any incomplete items before submitting your request.
                </Typography>
              )}
            </>
          ) : (
            <>
              <CheckCircle style={{ fontSize: 64, color: '#4caf50', marginBottom: 16 }} />
              <Typography variant="h5" gutterBottom style={{ color: '#2e7d32', fontWeight: 600 }}>
                Request Submitted Successfully!
              </Typography>

              {/* Request ID Display */}
              {submissionResult && submissionResult.requestId && (
                <Paper
                  style={{
                    padding: '16px',
                    margin: '16px auto',
                    maxWidth: '500px',
                    backgroundColor: '#f1f8e9',
                    border: '1px solid #c8e6c9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 500 }}>
                      Request ID:
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: 600, color: '#2e7d32', fontFamily: 'monospace' }}>
                      {submissionResult.requestId}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={handleCopyRequestId}
                    size="small"
                    style={{ marginLeft: 16 }}
                    title="Copy Request ID"
                  >
                    <FileCopy />
                  </IconButton>
                </Paper>
              )}

              <Typography variant="body1" color="textSecondary" paragraph style={{ maxWidth: 600, margin: '0 auto' }}>
                All configuration details have been gathered. Our SSO integration team will review your requirements
                and contact you within 1-2 business days to proceed with the integration setup.
              </Typography>
            </>
          )}
        </Paper>
      </div>

      {/* Add/Edit User Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCancelUser}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingIndex !== null ? "Edit Test User" : "Add Test User"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                variant="outlined"
                size="small"
                required
                placeholder="user@company.com"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom style={{ marginBottom: 8, fontWeight: 500 }}>
                Active Directory Groups
              </Typography>
              <Box style={{ marginBottom: 8, padding: 12, backgroundColor: '#e3f2fd', borderRadius: 4, border: '1px solid #90caf9' }}>
                <Typography variant="body2" style={{ color: '#1565c0', fontSize: '0.8rem' }}>
                  💡 <strong>Tip:</strong> Type a group name and press Enter to add it as a chip. Click the × on any chip to remove it.
                </Typography>
              </Box>
              <Box
                className={`${classes.inputContainer} ${focusedInput ? 'focused' : ''}`}
              >
                <Group color="action" style={{ marginRight: 8, fontSize: '1rem' }} />

                {/* Render existing groups as chips inside the container */}
                {(userFormData.adGroups && userFormData.adGroups.length > 0) && (
                  <>
                    {userFormData.adGroups.map((group, groupIndex) => (
                      <Chip
                        key={groupIndex}
                        label={group}
                        size="small"
                        color="primary"
                        className={classes.chipInside}
                        onDelete={() => handleRemoveGroup(groupIndex)}
                        deleteIcon={<Delete style={{ fontSize: '14px' }} />}
                        style={{ margin: 2 }}
                      />
                    ))}
                  </>
                )}

                {/* Input field inside the container */}
                <input
                  className={classes.inputField}
                  placeholder={(userFormData.adGroups && userFormData.adGroups.length > 0) ? "Type another group and press Enter..." : "Type group name and press Enter..."}
                  value={newGroupInput || ''}
                  onChange={(e) => handleGroupInputChange(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
              </Box>
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                Leave blank if user has no specific groups. Examples: Administrators, Users, Developers
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUser}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained" color="primary">
            {editingIndex !== null ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestingValidation;